import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext'; 
import LanguageToggle from '../components/LanguageToggle'; 
import { 
  Users, Play, Plus, ClipboardList, Target, 
  Search, Edit, ArrowRight, X, Globe, User, Star, Calendar, FileText, Trash2, BarChart2, AlertTriangle, LogOut, Printer, FolderKanban
} from 'lucide-react';
import TamkeenLogo from '../components/TamkeenLogo';
import { db } from '../firebase';
// 1. استيراد serverTimestamp لتوحيد الزمن المعماري
import { collection, onSnapshot, doc, setDoc, deleteDoc, updateDoc, query, where, serverTimestamp } from 'firebase/firestore';

const WorkshopWorkspace = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t, language } = useLanguage(); 
  const [activeTab, setActiveTab] = useState('library'); 
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState(''); // حالة محرك البحث

  // مستشعر التوجيه الذكي: لفتح تبويب "أنشطتي" تلقائياً عند القدوم من الاستوديو
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
      window.history.replaceState({}, document.title); // تنظيف المسار بعد فتحه
    }
  }, [location]);

  // توحيد الهوية الأمنية لمنع اختفاء الأنشطة
  const safeUserId = user?.uid || user?.id;
  const workshopData = {
    name: user?.workshop || t('educator.defaultWorkshop'),
    mentor: user?.name || t('educator.defaultMentor'),
  };

  const [beneficiaries, setBeneficiaries] = useState([]);
  const [groups, setGroups] = useState([]);
  const [sharedActivities, setSharedActivities] = useState([]);
  const [myActivities, setMyActivities] = useState([]);
  const [evaluationsHistory, setEvaluationsHistory] = useState([]);

  const [isLaunchModalOpen, setIsLaunchModalOpen] = useState(false);
  const [selectedActivityForLaunch, setSelectedActivityForLaunch] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState('');

  const [selectedEvalDetail, setSelectedEvalDetail] = useState(null);
  const [reportTimeframe, setReportTimeframe] = useState('month'); 

  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForm, setGroupForm] = useState({ name: '' });

  const [isBenModalOpen, setIsBenModalOpen] = useState(false);
  const [editingBen, setEditingBen] = useState(null);
  const [benForm, setBenForm] = useState({ name: '', condition: '', groupId: '' });

  useEffect(() => {
    if (!workshopData.name) return;

    const qGroups = query(collection(db, 'groups'), where('workshop', '==', workshopData.name));
    const unsubGroups = onSnapshot(qGroups, (snapshot) => {
      setGroups(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    const qBens = query(collection(db, 'beneficiaries'), where('workshop', '==', workshopData.name));
    const unsubBens = onSnapshot(qBens, (snapshot) => {
      setBeneficiaries(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    const qEvals = query(collection(db, 'evaluations_history'), where('workshop', '==', workshopData.name));
    const unsubEvals = onSnapshot(qEvals, (snapshot) => {
      setEvaluationsHistory(snapshot.docs.map(doc => ({ id: doc.id, sessionId: doc.id, ...doc.data() })));
    });

    const unsubLibrary = onSnapshot(collection(db, 'library'), (snapshot) => {
      setSharedActivities(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    
    return () => {
      unsubGroups();
      unsubBens();
      unsubEvals();
      unsubLibrary();
    };
  }, [workshopData.name]);

  const openAddGroup = () => { setEditingGroup(null); setGroupForm({ name: '' }); setIsGroupModalOpen(true); };
  const openEditGroup = (g) => { setEditingGroup(g); setGroupForm({ name: g.name }); setIsGroupModalOpen(true); };
  const saveGroup = async (e) => {
    e.preventDefault();
    if(!groupForm.name.trim()) return;
    
    if (editingGroup) {
      await updateDoc(doc(db, 'groups', editingGroup.id), { name: groupForm.name });
    } else {
      const newId = `g_${Date.now()}`;
      await setDoc(doc(db, 'groups', newId), { name: groupForm.name, workshop: workshopData.name });
    }
    setIsGroupModalOpen(false);
  };
  const deleteGroup = async (id) => {
    if(window.confirm(t('educator.alerts.deleteGroupConfirm'))) {
      await deleteDoc(doc(db, 'groups', id));
      const bensToUpdate = beneficiaries.filter(b => b.groupId === id);
      for (let b of bensToUpdate) {
         await updateDoc(doc(db, 'beneficiaries', b.id), { groupId: '' });
      }
    }
  };

  const openAddBeneficiary = () => { 
    if(groups.length === 0) { alert(t('educator.alerts.requireGroup')); return; }
    setEditingBen(null); setBenForm({ name: '', condition: '', groupId: groups[0].id }); setIsBenModalOpen(true); 
  };
  const openEditBeneficiary = (b) => { setEditingBen(b); setBenForm({ name: b.name, condition: b.condition, groupId: b.groupId || '' }); setIsBenModalOpen(true); };
  
  const saveBeneficiary = async (e) => {
    e.preventDefault();
    if(!benForm.name.trim() || !benForm.groupId) return;
    
    if (editingBen) {
      await updateDoc(doc(db, 'beneficiaries', editingBen.id), { ...benForm });
    } else {
      const newId = `b_${Date.now()}`;
      await setDoc(doc(db, 'beneficiaries', newId), { 
        ...benForm, 
        workshop: workshopData.name,
        // 2. ربط الانضمام بتوقيت الخادم المركزي لتجنب تضارب التواريخ
        createdAt: serverTimestamp() 
      });
    }
    setIsBenModalOpen(false);
  };

  const deleteBeneficiary = async (id) => {
    if(window.confirm(t('educator.alerts.deleteBenConfirm'))) {
      await deleteDoc(doc(db, 'beneficiaries', id));
    }
  };

  const handleLaunchActivity = () => {
    if (!selectedGroupId) return;
    const group = groups.find(g => g.id === selectedGroupId);
    const targetBeneficiaries = beneficiaries.filter(b => b.groupId === selectedGroupId);

    if (targetBeneficiaries.length === 0) { alert(t('educator.alerts.emptyGroup')); return; }

    // التدخل الجراحي: نزع الغلاف الثقيل لتخفيف الضغط على ذاكرة المتصفح قبل الإقلاع
    const activityPayload = { ...selectedActivityForLaunch };
    delete activityPayload.coverImage; 

    try {
      // تفريغ أجزاء من الذاكرة لتأمين مساحة، ثم رفع الحمولة
      localStorage.removeItem('tamken_lesson_draft');
      localStorage.setItem('tamkeen_live_activity', JSON.stringify(activityPayload));
    } catch (error) {
      alert("🚨 ذاكرة المتصفح ممتلئة جداً. سيتم تنظيفها إجبارياً لإقلاع النشاط.");
      localStorage.clear();
      localStorage.setItem('tamkeen_live_activity', JSON.stringify(activityPayload));
    }

    const sessionContext = {
      activityId: selectedActivityForLaunch.id,
      activityTitle: selectedActivityForLaunch.title,
      groupName: group.name,
      workshop: workshopData.name, 
      beneficiaries: targetBeneficiaries,
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem('tamkeen_active_session', JSON.stringify(sessionContext));
    setIsLaunchModalOpen(false);
    navigate('/viewer');
  };

  const handleDeleteEvaluation = async (sessionId) => {
    if (window.confirm(t('educator.alerts.deleteEvalConfirm'))) {
      await deleteDoc(doc(db, 'evaluations_history', sessionId));
    }
  };

  const handleDeleteActivity = async (id) => {
    if (window.confirm("هل أنت متأكد من حذف هذا النشاط نهائياً؟ سيتم حذفه من المنصة بأكملها.")) {
      await deleteDoc(doc(db, 'library', id));
    }
  };

  const getAnalyticsData = () => {
    const now = new Date();
    const filteredHistory = evaluationsHistory.filter(session => {
      let sessionDate;
      // 3. المحرك الجديد: قراءة توقيت الخادم الفعلي لمنع انهيار الإحصائيات (Causality Fix)
      if (session.createdAt && session.createdAt.toDate) {
        sessionDate = session.createdAt.toDate();
      } else if (session.date) {
        // حماية البيانات القديمة الوهمية
        const parts = session.date.split('/');
        sessionDate = parts.length === 3 ? new Date(parts[2], parts[1] - 1, parts[0]) : new Date();
      } else {
        sessionDate = new Date();
      }
      
      const diffDays = Math.ceil(Math.abs(now - sessionDate) / (1000 * 60 * 60 * 24));
      if (reportTimeframe === 'week') return diffDays <= 7;
      if (reportTimeframe === 'month') return diffDays <= 30;
      if (reportTimeframe === 'year') return diffDays <= 365;
      return true;
    });

    const stats = {};
    filteredHistory.forEach(session => {
      Object.entries(session.results).forEach(([bId, result]) => {
        if (!stats[bId]) stats[bId] = { totalScore: 0, count: 0, name: beneficiaries.find(b => b.id === bId)?.name || t('educator.evaluations.unknownBen') };
        if (result.rating > 0) { stats[bId].totalScore += result.rating; stats[bId].count += 1; }
      });
    });
    return Object.values(stats).map(s => {
      const avg = s.count > 0 ? (s.totalScore / s.count).toFixed(1) : 0;
      return { name: s.name, avg: avg, percentage: (avg / 5) * 100 };
    });
  };

  const printDocument = () => {
    window.print();
  };

  const renderLibrary = () => {
    const searchedLibrary = sharedActivities.filter(a => a.title.includes(searchQuery));
    return (
      <div className="space-y-6 print:hidden">
        <div className="flex flex-col md:flex-row justify-between items-center bg-white p-6 rounded-3xl border border-slate-200 shadow-sm mb-8 gap-4">
           <div>
             <h2 className="text-xl font-extrabold text-slate-800 flex items-center gap-2 mb-1"><Globe className="w-6 h-6 text-blue-600" /> {t('educator.tabLibrary')}</h2>
             <p className="text-sm text-slate-500">تصفح واستخدم الأنشطة المتاحة لجميع أطر المركز</p>
           </div>
           {/* محرك البحث الأنيق */}
           <div className="relative w-full md:w-72">
             <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
             <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="ابحث عن نشاط..." className="w-full bg-slate-50 border border-slate-200 text-slate-700 text-sm font-bold rounded-full py-3 pr-12 pl-4 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all shadow-inner" />
           </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {searchedLibrary.length > 0 ? searchedLibrary.map(activity => (
            <motion.div key={activity.id} whileHover={{ y: -5 }} className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden flex flex-col group">
              <div className="h-40 w-full relative overflow-hidden bg-slate-100">
                 <img src={activity.coverImage} alt={activity.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                 <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
                 <span className="absolute top-3 end-3 bg-blue-600/90 backdrop-blur text-white text-[10px] font-bold px-3 py-1.5 rounded-lg border border-blue-500/50">{activity.template?.name || t('educator.library.noTemplate')}</span>
                 <div className="absolute bottom-3 end-3 flex items-center gap-1.5 text-slate-200 text-xs font-medium"><User className="w-3.5 h-3.5" /> {activity.author}</div>
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <h3 className="text-lg font-bold text-slate-800 mb-4 line-clamp-2 leading-snug">{activity.title}</h3>
                <div className="mt-auto pt-4 border-t border-slate-100 flex gap-2">
                  <button onClick={() => { setSelectedActivityForLaunch(activity); setIsLaunchModalOpen(true); }} className="flex-1 bg-slate-900 text-white py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 hover:bg-blue-600 transition-colors shadow-md">
                    <Play className="w-4 h-4 fill-current" /> بدء النشاط
                  </button>
                </div>
              </div>
            </motion.div>
          )) : (
            <div className="col-span-full py-16 text-center border-2 border-dashed border-slate-300 rounded-3xl text-slate-500">
               <Search className="w-12 h-12 mx-auto mb-3 opacity-20" />
               <p className="font-bold">لم يتم العثور على أنشطة.</p>
            </div>
          )}
        </div>
      </div>
    );
  };
  const renderMyActivities = () => {
    // إصلاح الهوية والفلترة مع محرك البحث
    const myRawActivities = sharedActivities.filter(activity => activity.creatorId === safeUserId);
    const searchedMyActivities = myRawActivities.filter(a => a.title.includes(searchQuery));
    
    return (
      <div className="space-y-6 print:hidden">
        <div className="flex flex-col lg:flex-row justify-between items-center bg-white p-6 rounded-3xl border border-slate-200 shadow-sm mb-8 gap-4">
           <div>
             <h2 className="text-xl font-extrabold text-slate-800 flex items-center gap-2 mb-1"><FolderKanban className="w-6 h-6 text-amber-500" /> أنشطتي</h2>
             <p className="text-sm text-slate-500">إدارة الأنشطة التي قمت بتصميمها (تعديل وحذف)</p>
           </div>
           <div className="flex items-center gap-4 w-full lg:w-auto">
             <div className="relative w-full lg:w-64">
               <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
               <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="ابحث في أنشطتك..." className="w-full bg-slate-50 border border-slate-200 text-slate-700 text-sm font-bold rounded-full py-3 pr-12 pl-4 focus:outline-none focus:ring-2 focus:ring-amber-500 transition-all shadow-inner" />
             </div>
             <button onClick={() => {
                 localStorage.removeItem('tamken_lesson_draft');
                 navigate('/builder');
             }} className="flex items-center shrink-0 gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white rounded-full font-black shadow-lg shadow-amber-500/30 transition-all">
               <Plus className="w-5 h-5" /> نشاط جديد
             </button>
           </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {searchedMyActivities.length > 0 ? searchedMyActivities.map(activity => (
            <motion.div key={activity.id} whileHover={{ y: -5 }} className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden flex flex-col group">
              <div className="h-40 w-full relative overflow-hidden bg-slate-100 flex items-center justify-center">
                 {activity.coverImage ? (
                   <img src={activity.coverImage} alt={activity.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                 ) : (
                   <div className="w-full h-full bg-indigo-50 flex items-center justify-center"><FileText className="w-12 h-12 text-indigo-200" /></div>
                 )}
                 <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
                 <span className="absolute top-3 end-3 bg-amber-500/90 backdrop-blur text-white text-[10px] font-bold px-3 py-1.5 rounded-lg border border-amber-400/50">
                   {activity.type === 'lesson' ? 'نشاط مدمج' : (activity.template?.name || t('educator.library.noTemplate'))}
                 </span>
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <h3 className="text-lg font-bold text-slate-800 mb-4 line-clamp-2 leading-snug">{activity.title}</h3>
                <div className="mt-auto pt-4 border-t border-slate-100 flex gap-2">
                  <button onClick={() => { setSelectedActivityForLaunch(activity); setIsLaunchModalOpen(true); }} className="flex-1 bg-slate-900 text-white py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 hover:bg-amber-500 transition-colors shadow-md">
                    <Play className="w-4 h-4 fill-current" /> بدء
                  </button>
                  <button onClick={() => handleDeleteActivity(activity.id)} className="p-2.5 bg-red-50 text-red-600 hover:bg-red-500 hover:text-white rounded-xl transition-colors border border-red-100 hover:border-transparent" title="حذف نهائي">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          )) : (
            <div className="col-span-full py-16 text-center border-2 border-dashed border-slate-300 rounded-3xl text-slate-500">
               <FolderKanban className="w-12 h-12 mx-auto mb-3 opacity-20" />
               <p className="font-bold">لم يتم العثور على أنشطة خاصة بك.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderGroups = () => (
    <div className="space-y-6 print:hidden">
      <div className="flex justify-between items-center bg-white p-6 rounded-3xl border border-slate-200 shadow-sm mb-6">
        <div>
          <h2 className="text-xl font-extrabold text-slate-800 flex items-center gap-2 mb-1"><Target className="w-6 h-6 text-indigo-600" /> {t('educator.tabGroups')}</h2>
          <p className="text-sm text-slate-500">{t('educator.groups.subtitle')}</p>
        </div>
        <button onClick={openAddGroup} className="bg-indigo-600 hover:bg-indigo-500 text-white px-5 py-2.5 rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-indigo-600/20 transition-all"><Plus className="w-4 h-4" /> {t('educator.groups.createBtn')}</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {groups.length > 0 ? groups.map(group => {
          const groupMembers = beneficiaries.filter(b => b.groupId === group.id);
          return (
            <motion.div key={group.id} whileHover={{ y: -5 }} className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm flex flex-col group cursor-default">
              <div className="flex justify-between items-start mb-4">
                <div className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600"><Users className="w-6 h-6" /></div>
                <div className="flex items-center gap-2">
                  <button onClick={() => openEditGroup(group)} className="p-2 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"><Edit className="w-4 h-4" /></button>
                  <button onClick={() => deleteGroup(group.id)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <h3 className="text-lg font-bold text-slate-800 mb-1">{group.name}</h3>
              <p className="text-slate-500 text-sm mb-4 font-bold flex items-center gap-1"><User className="w-3.5 h-3.5"/> {groupMembers.length} {t('educator.groups.membersCount')}</p>
              <div className="flex flex-wrap gap-2 mt-auto pt-4 border-t border-slate-100">
                {groupMembers.length > 0 ? groupMembers.map(b => (<span key={b.id} className="bg-slate-50 text-xs font-bold text-slate-600 px-3 py-1.5 rounded-lg border border-slate-200">{b.name}</span>)) : (<span className="text-xs text-slate-400 italic">{t('educator.groups.noMembers')}</span>)}
              </div>
            </motion.div>
          );
        }) : (
          <div className="col-span-full py-16 text-center border-2 border-dashed border-slate-300 rounded-3xl text-slate-500">
             <Target className="w-12 h-12 mx-auto mb-3 opacity-20" />
             <p className="font-bold">{t('educator.groups.empty')}</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderBeneficiaries = () => (
    <div className="space-y-6 print:hidden">
      <div className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
          <div>
            <h2 className="text-xl font-extrabold text-slate-800 flex items-center gap-2 mb-1"><Users className="w-6 h-6 text-emerald-600" /> {t('educator.tabBeneficiaries')}</h2>
            <p className="text-sm text-slate-500">{t('educator.beneficiaries.subtitle')}</p>
          </div>
          <button onClick={openAddBeneficiary} className="text-sm font-bold text-white bg-emerald-600 px-5 py-2.5 rounded-xl flex items-center gap-2 hover:bg-emerald-500 shadow-lg shadow-emerald-600/20 transition-all"><Plus className="w-4 h-4" /> {t('educator.beneficiaries.addBtn')}</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-start text-sm">
            <thead className="bg-white text-slate-500 border-b border-slate-200">
              <tr>
                <th className="p-5 font-bold">{t('educator.beneficiaries.colName')}</th>
                <th className="p-5 font-bold">{t('educator.beneficiaries.colCondition')}</th>
                <th className="p-5 font-bold">{t('educator.beneficiaries.colGroup')}</th>
                <th className="p-5 font-bold text-center">{t('educator.beneficiaries.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {beneficiaries.length > 0 ? beneficiaries.map((b) => {
                const groupName = groups.find(g => g.id === b.groupId)?.name || t('educator.beneficiaries.unassigned');
                return (
                  <tr key={b.id} className="border-b border-slate-50 hover:bg-slate-50/80 transition-colors text-slate-700">
                    <td className="p-5 font-bold text-slate-900">{b.name}</td>
                    <td className="p-5"><span className="bg-indigo-50 text-indigo-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-indigo-100">{b.condition}</span></td>
                    <td className="p-5 font-bold text-emerald-600">{groupName}</td>
                    <td className="p-5 text-center flex justify-center gap-2">
                      <button onClick={() => openEditBeneficiary(b)} className="p-2 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"><Edit className="w-4 h-4" /></button>
                      <button onClick={() => deleteBeneficiary(b.id)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                );
              }) : (
                <tr><td colSpan="4" className="p-10 text-center text-slate-400 font-bold">{t('educator.beneficiaries.empty')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderEvaluations = () => {
    if (evaluationsHistory.length === 0) {
      return (
        <div className="bg-white border border-slate-200 p-12 rounded-3xl shadow-sm text-center flex flex-col items-center justify-center min-h-[400px]">
          <ClipboardList className="w-16 h-16 text-slate-300 mb-4" />
          <h2 className="text-xl font-bold text-slate-700 mb-2">{t('educator.evaluations.emptyTitle')}</h2>
          <p className="text-slate-500">{t('educator.evaluations.emptySubtitle')}</p>
        </div>
      );
    }
    const analyticsData = getAnalyticsData();
    return (
      <div className="space-y-8 print:space-y-4">
        
        <div className="bg-white border border-slate-200 rounded-3xl shadow-sm p-6 print:border-none print:shadow-none print:p-0 print:mb-8">
          <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4 print:mb-4">
            <h2 className="text-lg font-extrabold text-slate-800 flex items-center gap-2 print:text-black">
              <BarChart2 className="w-5 h-5 text-indigo-600 print:hidden" /> 
              {t('educator.evaluations.progressTitle')}
            </h2>
            <div className="flex bg-slate-100 p-1 rounded-xl print:hidden">
              <button onClick={() => setReportTimeframe('week')} className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${reportTimeframe === 'week' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>{t('educator.evaluations.week')}</button>
              <button onClick={() => setReportTimeframe('month')} className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${reportTimeframe === 'month' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>{t('educator.evaluations.month')}</button>
              <button onClick={() => setReportTimeframe('year')} className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${reportTimeframe === 'year' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>{t('educator.evaluations.year')}</button>
            </div>
          </div>
          <div className="space-y-4 print:space-y-3">
            {analyticsData.length > 0 ? analyticsData.map((stat, idx) => (
              <div key={idx} className="flex items-center gap-4 print:gap-2 print:break-inside-avoid">
                <div className="w-32 shrink-0 text-sm font-bold text-slate-700 truncate print:text-black print:text-xs" title={stat.name}>{stat.name}</div>
                <div className="flex-1 bg-slate-100 h-6 rounded-full overflow-hidden relative border border-slate-200 print:border-black print:h-5 print:rounded-none" dir="ltr">
                  <motion.div 
                    initial={{ width: 0 }} 
                    animate={{ width: `${stat.percentage}%` }} 
                    transition={{ duration: 1, ease: "easeOut" }} 
                    className="h-full bg-gradient-to-r from-blue-400 to-indigo-500 print:bg-slate-300"
                    style={{ WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}
                  />
                </div>
                <div className="w-12 shrink-0 text-sm font-bold text-slate-800 flex justify-end gap-1 print:text-black print:text-xs">
                  {stat.avg} <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400 mt-0.5 print:hidden" />
                </div>
              </div>
            )) : <p className="text-center text-slate-500 text-sm py-4">{t('educator.evaluations.noData')}</p>}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden print:border-none print:shadow-none">
          <div className="p-6 border-b border-slate-100 flex justify-between items-center print:border-black print:p-0 print:mb-4 print:pb-2">
             <h2 className="text-lg font-extrabold text-slate-800 flex items-center gap-2 print:text-black">
               <ClipboardList className="w-5 h-5 text-indigo-600 print:hidden" /> 
               {t('educator.evaluations.historyTitle')}
             </h2>
             <button onClick={printDocument} className="print:hidden flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold text-sm transition-colors border border-slate-200">
               <Printer className="w-4 h-4" /> طباعة السجل
             </button>
          </div>
          <table className="w-full text-start text-sm print:text-black print:text-xs">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-100 print:bg-white print:border-b-2 print:border-black">
              <tr>
                <th className="p-5 font-bold print:p-2">{t('educator.evaluations.colDate')}</th>
                <th className="p-5 font-bold print:p-2">{t('educator.evaluations.colActivity')}</th>
                <th className="p-5 font-bold print:p-2">{t('educator.evaluations.colGroup')}</th>
                <th className="p-5 font-bold text-center print:p-2">{t('educator.evaluations.colScore')}</th>
                <th className="p-5 font-bold text-center print:hidden">{t('educator.evaluations.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {evaluationsHistory.map((session) => {
                const scores = Object.values(session.results).map(r => r.rating);
                const avgScore = scores.length ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1) : 0;
                // 5. استخراج التوقيت الفعلي للعرض بصيغة مستقرة
                const displayDate = session.createdAt?.toDate ? session.createdAt.toDate().toLocaleDateString('en-GB') : session.date;
                
                return (
                  <tr key={session.sessionId} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors text-slate-700 print:border-b print:border-slate-300 print:break-inside-avoid">
                    <td className="p-5 font-bold text-slate-600 flex items-center gap-2 print:p-2 print:text-black"><Calendar className="w-4 h-4 text-slate-400 print:hidden"/> {displayDate}</td>
                    <td className="p-5 font-bold text-slate-900 print:p-2 print:text-black">{session.activityTitle}</td>
                    <td className="p-5 print:p-2"><span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-lg text-xs font-bold border border-blue-100 print:border-none print:bg-transparent print:p-0 print:text-black">{session.groupName}</span></td>
                    <td className="p-5 text-center print:p-2"><div className="inline-flex items-center gap-1 bg-yellow-50 text-yellow-700 px-3 py-1 rounded-lg font-bold border border-yellow-200 print:border-none print:bg-transparent print:p-0 print:text-black"><Star className="w-4 h-4 fill-yellow-400 text-yellow-400 print:hidden" /> {avgScore}</div></td>
                    <td className="p-5 flex justify-center items-center gap-2 print:hidden">
                      <button onClick={() => setSelectedEvalDetail(session)} className="text-indigo-600 hover:text-white bg-indigo-50 hover:bg-indigo-600 px-4 py-2 rounded-xl text-xs font-bold transition-colors border border-indigo-100 hover:border-transparent">{t('educator.evaluations.detailsBtn')}</button>
                      <button onClick={() => handleDeleteEvaluation(session.sessionId)} className="text-red-500 hover:text-white bg-red-50 hover:bg-red-500 p-2 rounded-xl transition-colors border border-red-100 hover:border-transparent" title={t('admin.modals.deleteBtn')}><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 flex font-sans relative overflow-hidden print:bg-white print:p-0">
      <div className="print:hidden"><LanguageToggle /></div>

      <aside className="w-72 bg-white border-e border-slate-200 flex flex-col justify-between z-20 shadow-xl shrink-0 pt-16 print:hidden">
        <div>
          <div className="p-8 border-b border-slate-100 flex flex-col items-center text-center">
            <TamkeenLogo className="w-14 h-14 mb-4" />
            <span className="text-xs font-bold text-blue-600 bg-blue-50 px-3 py-1 rounded-full mb-2">{t('educator.sidebarRole')}</span>
            <h2 className="text-xl font-bold text-slate-800">{workshopData.name}</h2>
          </div>
          <nav className="p-4 space-y-2">
            <SidebarNavItem icon={<Globe />} label={t('educator.tabLibrary')} active={activeTab === 'library'} onClick={() => setActiveTab('library')} />
            <SidebarNavItem icon={<FolderKanban />} label="أنشطتي" active={activeTab === 'my_activities'} onClick={() => setActiveTab('my_activities')} />
            <SidebarNavItem icon={<Target />} label={t('educator.tabGroups')} active={activeTab === 'groups'} onClick={() => setActiveTab('groups')} />
            <SidebarNavItem icon={<Users />} label={t('educator.tabBeneficiaries')} active={activeTab === 'beneficiaries'} onClick={() => setActiveTab('beneficiaries')} />
            <SidebarNavItem icon={<ClipboardList />} label={t('educator.tabEvaluations')} active={activeTab === 'evaluations'} onClick={() => setActiveTab('evaluations')} />
          </nav>
        </div>
        <div className="p-6 border-t border-slate-100 text-center">
            <div className="text-sm font-bold text-slate-800 mb-4">{workshopData.mentor}</div>
            <button onClick={logout} className="w-full flex items-center justify-center gap-3 px-4 py-2 text-red-500 hover:bg-red-50 rounded-xl transition-all font-medium text-sm border border-transparent hover:border-red-100">
                <LogOut className={`w-4 h-4 ${language === 'fr' ? 'rotate-180' : ''}`} /> {t('educator.logout')}
            </button>
        </div>
      </aside>

      <main className="flex-1 p-10 overflow-y-auto relative pt-20 print:p-0 print:w-full print:absolute print:top-0 print:left-0 print:m-0 print:overflow-visible">
        <header className="flex justify-between items-center mb-10 print:hidden">
          <div>
            <h1 className="text-3xl font-extrabold text-slate-900 mb-2">
              {activeTab === 'library' ? t('educator.headerTitles.library') : activeTab === 'my_activities' ? 'إدارة أنشطتي الخاصة' : activeTab === 'groups' ? t('educator.headerTitles.groups') : activeTab === 'beneficiaries' ? t('educator.headerTitles.beneficiaries') : t('educator.headerTitles.evaluations')}
            </h1>
            <p className="text-slate-500">{t('educator.headerSubtitle')}</p>
          </div>
        </header>

        <div className="hidden print:block text-center mb-10 border-b-2 border-black pb-4">
           <h1 className="text-2xl font-bold">مركز محمد السادس للاشخاص في وضعية اعاقة - سلا</h1>
           <h2 className="text-xl mt-2">السجل البيداغوجي لورشة: {workshopData.name}</h2>
           <p className="text-sm mt-1">تأطير: {workshopData.mentor}</p>
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
            {activeTab === 'library' && renderLibrary()}
            {activeTab === 'my_activities' && renderMyActivities()}
            {activeTab === 'groups' && renderGroups()}
            {activeTab === 'beneficiaries' && renderBeneficiaries()}
            {activeTab === 'evaluations' && renderEvaluations()}
          </motion.div>
        </AnimatePresence>
      </main>

      <AnimatePresence>
        {isGroupModalOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 print:hidden">
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }} className="bg-white border border-slate-200 rounded-3xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col">
              <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2"><Target className="w-6 h-6 text-indigo-600" /> {editingGroup ? t('educator.modals.editGroup') : t('educator.modals.newGroup')}</h2>
                <button onClick={() => setIsGroupModalOpen(false)} className="text-slate-400 hover:text-slate-800 p-2 rounded-xl hover:bg-slate-200 transition-colors"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={saveGroup} className="p-6 space-y-5">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">{t('educator.modals.groupName')}</label>
                  <input type="text" value={groupForm.name} onChange={(e) => setGroupForm({ name: e.target.value })} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:outline-none" placeholder={t('educator.modals.groupNamePlace')} required />
                </div>
                <button type="submit" className="w-full py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold shadow-lg shadow-indigo-600/20 transition-all mt-4">{t('educator.modals.saveGroup')}</button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isBenModalOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 print:hidden">
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }} className="bg-white border border-slate-200 rounded-3xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col">
              <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2"><User className="w-6 h-6 text-emerald-600" /> {editingBen ? t('educator.modals.editBen') : t('educator.modals.newBen')}</h2>
                <button onClick={() => setIsBenModalOpen(false)} className="text-slate-400 hover:text-slate-800 p-2 rounded-xl hover:bg-slate-200 transition-colors"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={saveBeneficiary} className="p-6 space-y-5">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">{t('educator.modals.benName')}</label>
                  <input type="text" value={benForm.name} onChange={(e) => setBenForm({ ...benForm, name: e.target.value })} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none" placeholder={t('educator.modals.benNamePlace')} required />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">{t('educator.modals.benCondition')}</label>
                  <input type="text" value={benForm.condition} onChange={(e) => setBenForm({ ...benForm, condition: e.target.value })} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none" placeholder={t('educator.modals.benConditionPlace')} required />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">{t('educator.modals.benGroup')}</label>
                  <select value={benForm.groupId} onChange={(e) => setBenForm({ ...benForm, groupId: e.target.value })} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-none" required>
                    <option value="" disabled>{t('educator.modals.selectGroup')}</option>
                    {groups.map(g => (<option key={g.id} value={g.id}>{g.name}</option>))}
                  </select>
                </div>
                <button type="submit" className="w-full py-4 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold shadow-lg shadow-emerald-600/20 transition-all mt-4">{t('educator.modals.saveBen')}</button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isLaunchModalOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 print:hidden">
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-white border border-slate-200 rounded-3xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col">
              <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2"><Play className="w-5 h-5 text-blue-600 fill-current" /> {t('educator.modals.launchTitle')}</h2>
                <button onClick={() => setIsLaunchModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded-md hover:bg-slate-200 transition-colors"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-6">
                <div className="mb-6 flex gap-4 items-center bg-blue-50 p-3 rounded-2xl border border-blue-100">
                  <img src={selectedActivityForLaunch?.coverImage} className="w-16 h-16 rounded-xl object-cover" alt="غلاف" />
                  <div>
                    <p className="text-xs font-bold text-blue-600 mb-1">{t('educator.modals.selectedActivity')}</p>
                    <p className="text-sm font-extrabold text-slate-800 line-clamp-2">{selectedActivityForLaunch?.title}</p>
                  </div>
                </div>
                
                {groups.length === 0 ? (
                   <div className="bg-amber-50 border border-amber-200 text-amber-700 p-4 rounded-xl text-sm font-bold flex gap-2">
                     <AlertTriangle className="w-5 h-5 shrink-0"/>
                     {t('educator.modals.noGroupsWarning')}
                   </div>
                ) : (
                  <>
                    <label className="block text-sm font-bold text-slate-700 mb-3">{t('educator.modals.selectGroupLabel')}</label>
                    <div className="space-y-3 max-h-60 overflow-y-auto pe-2">
                      {groups.map(g => {
                        const groupMembers = beneficiaries.filter(b => b.groupId === g.id);
                        return (
                          <label key={g.id} className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${selectedGroupId === g.id ? 'bg-indigo-50 border-indigo-600' : 'bg-white border-slate-200 hover:border-slate-300'}`}>
                            <input type="radio" name="group_select" value={g.id} checked={selectedGroupId === g.id} onChange={() => setSelectedGroupId(g.id)} className="w-4 h-4 text-indigo-600 bg-white border-slate-300 focus:ring-indigo-600 focus:ring-2" />
                            <div className="flex-1">
                              <span className="block text-sm font-bold text-slate-800">{g.name}</span>
                              <span className="block text-xs font-bold text-slate-500 mt-1">{groupMembers.length} {t('educator.groups.membersCount')}</span>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
              <div className="p-6 border-t border-slate-100 bg-slate-50">
                <button onClick={handleLaunchActivity} disabled={!selectedGroupId} className={`w-full py-3.5 rounded-xl font-bold text-base flex items-center justify-center gap-2 transition-all ${selectedGroupId ? 'bg-slate-900 hover:bg-slate-800 text-white shadow-lg shadow-slate-900/20' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}>
                  {t('educator.modals.confirmLaunch')} <ArrowRight className={`w-5 h-5 ${language === 'fr' ? 'rotate-180' : ''}`} />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selectedEvalDetail && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 md:p-10 print:hidden">
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }} className="bg-white border border-slate-200 rounded-3xl w-full max-w-4xl shadow-2xl overflow-hidden flex flex-col max-h-full">
              <div className="p-6 border-b border-slate-100 flex justify-between items-start bg-slate-50 shrink-0">
                <div>
                  <h2 className="text-xl font-extrabold text-slate-800 flex items-center gap-2 mb-2"><FileText className="w-6 h-6 text-indigo-600" /> {t('educator.modals.evalDetailTitle')}</h2>
                  <div className="flex gap-4 text-sm font-bold text-slate-500">
                    <span className="flex items-center gap-1"><Play className="w-4 h-4"/> {selectedEvalDetail.activityTitle}</span>
                    <span className="flex items-center gap-1 border-e border-slate-300 pe-4"><Users className="w-4 h-4"/> {selectedEvalDetail.groupName}</span>
                    <span className="flex items-center gap-1 border-e border-slate-300 pe-4"><Calendar className="w-4 h-4"/> {selectedEvalDetail.createdAt?.toDate ? selectedEvalDetail.createdAt.toDate().toLocaleDateString('en-GB') : selectedEvalDetail.date}</span>
                  </div>
                </div>
                <button onClick={() => setSelectedEvalDetail(null)} className="text-slate-400 hover:text-red-500 p-2 rounded-xl hover:bg-red-50 transition-colors bg-white border border-slate-200"><X className="w-6 h-6" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50">
                <div className="space-y-4">
                  {Object.entries(selectedEvalDetail.results).map(([beneficiaryId, result]) => {
                    const beneficiary = beneficiaries.find(b => b.id === beneficiaryId) || { name: t('educator.evaluations.unknownBen'), condition: '-' };
                    return (
                      <div key={beneficiaryId} className="bg-white border border-slate-200 p-5 rounded-2xl flex flex-col md:flex-row gap-6 shadow-sm hover:border-indigo-200 transition-colors">
                        <div className="w-full md:w-1/4 shrink-0">
                          <p className="font-bold text-slate-800 text-lg mb-1">{beneficiary.name}</p>
                          <span className="bg-slate-100 text-slate-500 px-2 py-1 rounded-md text-[10px] font-bold border border-slate-200">{beneficiary.condition}</span>
                        </div>
                        <div className="flex flex-col justify-center border-t md:border-t-0 md:border-e border-slate-100 pt-4 md:pt-0 pe-0 md:pe-6 shrink-0">
                           <p className="text-xs font-bold text-slate-400 mb-2">{t('educator.modals.responseLevel')}</p>
                           <div className="flex gap-1" dir="ltr">
                             {[1, 2, 3, 4, 5].map((starValue) => (
                               <Star key={starValue} className={`w-6 h-6 ${starValue <= result.rating ? 'fill-yellow-400 text-yellow-400' : 'fill-slate-100 text-slate-300'}`} />
                             ))}
                           </div>
                        </div>
                        <div className="flex-1 border-t md:border-t-0 md:border-e border-slate-100 pt-4 md:pt-0 pe-0 md:pe-6">
                          <p className="text-xs font-bold text-slate-400 mb-2">{t('educator.modals.mentorNotes')}</p>
                          <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-xl border border-slate-100 min-h-[3rem]">
                            {result.note || <span className="text-slate-400 italic">{t('educator.modals.noNotes')}</span>}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const SidebarNavItem = ({ icon, label, active, onClick }) => (
  <button onClick={onClick} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${active ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-800'}`}>
    {React.cloneElement(icon, { className: "w-5 h-5" })}
    <span className="font-bold text-sm">{label}</span>
  </button>
);

export default WorkshopWorkspace;